/**
 * GameLoop - Fixed Timestep Game Loop Manager
 *
 * Implements a fixed timestep game loop with accumulator pattern.
 * This ensures consistent physics simulation regardless of frame rate,
 * while allowing smooth rendering via interpolation.
 *
 * Features:
 * - Fixed timestep updates (default 60 FPS physics)
 * - Accumulator prevents runaway catch-up after tab becomes visible
 * - Separate update and render callbacks
 * - Start/stop/pause controls
 *
 * Usage:
 *   const loop = new GameLoop(updateFn, renderFn);
 *   loop.start();
 */

import { GameState } from './GameState.js';

class GameLoop {
  /**
   * Create a new game loop
   * @param {Function} updateCallback - Called at fixed timestep (dt) for game logic
   * @param {Function} renderCallback - Called every frame (currentTime, alpha) for rendering
   * @param {Object} options - Configuration options
   * @param {number} options.fixedDt - Fixed timestep in seconds (default: 1/60)
   * @param {number} options.maxCatchupFrames - Max frames to catch up after pause (default: 4)
   */
  constructor(updateCallback, renderCallback, options = {}) {
    this.updateCallback = updateCallback;
    this.renderCallback = renderCallback;

    // Configuration
    this.fixedDt = options.fixedDt || GameState.FIXED_DT || (1 / 60);
    this.maxCatchupFrames = options.maxCatchupFrames || 4;

    // State
    this.lastTime = 0;
    this.accumulator = 0;
    this.running = false;
    this.rafId = null;

    // Pre-frame callback (e.g., for polling gamepad)
    this.preFrameCallback = null;

    // Bind the loop method to preserve context
    this.loop = this.loop.bind(this);
  }

  /**
   * Set a callback to run at the start of each frame
   * Useful for polling input devices like gamepads
   * @param {Function} callback
   */
  setPreFrameCallback(callback) {
    this.preFrameCallback = callback;
  }

  /**
   * Start the game loop
   */
  start() {
    if (this.running) return;

    this.running = true;
    this.lastTime = performance.now();
    this.accumulator = 0;
    this.rafId = requestAnimationFrame(this.loop);
  }

  /**
   * Stop the game loop
   */
  stop() {
    this.running = false;
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  /**
   * Reset timing state
   * Call this when returning from a paused/hidden state to prevent catch-up
   */
  resetTiming() {
    this.lastTime = performance.now();
    this.accumulator = 0;
  }

  /**
   * Main loop function - called every animation frame
   * @param {number} time - Current timestamp from requestAnimationFrame
   */
  loop(time) {
    if (!this.running) return;

    // Run pre-frame callback (e.g., gamepad polling)
    if (this.preFrameCallback) {
      this.preFrameCallback();
    }

    // Calculate delta time
    const dt = (time - this.lastTime) / 1000;
    this.lastTime = time;
    this.accumulator += dt;

    // Cap accumulator to prevent runaway catch-up after tab becomes visible again
    // Without this, returning from a backgrounded tab causes 60+ updates in rapid succession,
    // flooding the input history and creating server desync
    const maxAccumulator = this.fixedDt * this.maxCatchupFrames;
    if (this.accumulator > maxAccumulator) {
      this.accumulator = maxAccumulator;
    }

    // Fixed timestep updates
    while (this.accumulator >= this.fixedDt) {
      this.updateCallback(this.fixedDt);
      this.accumulator -= this.fixedDt;
    }

    // Calculate interpolation alpha for smooth rendering
    // Alpha represents how far we are between physics steps
    const alpha = this.accumulator / this.fixedDt;

    // Render with current time and interpolation alpha
    this.renderCallback(performance.now(), alpha);

    // Schedule next frame
    this.rafId = requestAnimationFrame(this.loop);
  }

  /**
   * Check if the loop is currently running
   * @returns {boolean}
   */
  isRunning() {
    return this.running;
  }

  /**
   * Get current fixed timestep
   * @returns {number} Fixed timestep in seconds
   */
  getFixedDt() {
    return this.fixedDt;
  }
}

// Export for use in other modules (when using ES modules)
// For now, GameLoop is a global class loaded via script tag

export { GameLoop };
